import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Chip,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import RoutePaths from '../routes/RoutePaths';
import SortableTableHeaderCell from './SortableTableHeaderCell';
import { applySortDirection, compareDates, compareNumbers, compareStrings } from './tableSort';

const PAGE_SIZE = 25;

const STATUS_CONFIG = {
    success: { color: 'success', labelId: 'monitoring.status.healthy' },
    error: { color: 'error', labelId: 'monitoring.status.error' },
    running: { color: 'warning', labelId: 'monitoring.source.status.running' },
};

const STATUS_ORDER = { error: 0, running: 1, success: 2 };

function getRunDisplayStatus(run) {
    if (run?.status === 'running') {
        return 'running';
    }
    if (run?.status === 'failed' || run?.able_to_fetch_data === false) {
        return 'error';
    }
    return 'success';
}

const NUMERIC_COLUMNS = [
    { id: 'itemsFound', key: 'items_found' },
    { id: 'itemsNew', key: 'items_new', highlightColor: 'success.main' },
    { id: 'itemsSkipped', key: 'items_skipped' },
    { id: 'itemsFailed', key: 'items_failed', highlightColor: 'error.main' },
];

const NUMERIC_COLUMN_IDS = new Set(NUMERIC_COLUMNS.map((column) => column.id));

function getRunDetailPath(mediaId, sourceId, runId) {
    return RoutePaths.monitoring.run
        .replace(':mediaId', encodeURIComponent(mediaId))
        .replace(':sourceId', encodeURIComponent(sourceId))
        .replace(':runId', encodeURIComponent(runId));
}

function StatusChip({ status }) {
    const intl = useIntl();
    const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.success;
    return (
        <Chip
            size="small"
            color={config.color}
            variant="outlined"
            label={intl.formatMessage({ id: config.labelId })}
        />
    );
}

function formatDateTime(value, locale) {
    if (!value) return '–';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '–';
    return date.toLocaleString(locale ?? 'fi-FI', {
        dateStyle: 'short',
        timeStyle: 'short',
    });
}

function NumberCell({ value, highlightColor }) {
    const numeric = typeof value === 'number' ? value : null;
    const isHighlighted = highlightColor && numeric > 0;
    return (
        <TableCell align="right" sx={{ borderColor: 'divider' }}>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: isHighlighted ? 600 : 400,
                    color: isHighlighted ? highlightColor : 'text.primary',
                }}
            >
                {numeric ?? '–'}
            </Typography>
        </TableCell>
    );
}

function ErrorMessageCell({ value }) {
    const errorText = value ?? '';

    return (
        <TableCell sx={{ borderColor: 'divider', maxWidth: 240, overflow: 'hidden' }}>
            <Tooltip title={errorText} arrow disableHoverListener={!errorText}>
                <Typography
                    variant="body2"
                    color="error.main"
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {errorText}
                </Typography>
            </Tooltip>
        </TableCell>
    );
}

function compareRunRows(a, b, orderBy) {
    if (orderBy === 'started') {
        return compareDates(a?.run_started_at, b?.run_started_at);
    }
    if (orderBy === 'finished') {
        return compareDates(a?.run_finished_at, b?.run_finished_at);
    }
    if (orderBy === 'status') {
        return compareNumbers(
            STATUS_ORDER[getRunDisplayStatus(a)] ?? 99,
            STATUS_ORDER[getRunDisplayStatus(b)] ?? 99
        );
    }
    if (orderBy === 'ableToFetch') {
        const aVal = a?.able_to_fetch_data === true ? 1 : a?.able_to_fetch_data === false ? 0 : -1;
        const bVal = b?.able_to_fetch_data === true ? 1 : b?.able_to_fetch_data === false ? 0 : -1;
        return compareNumbers(aVal, bVal);
    }
    if (orderBy === 'error') {
        return compareStrings(a?.error_summary, b?.error_summary);
    }
    if (NUMERIC_COLUMN_IDS.has(orderBy)) {
        const column = NUMERIC_COLUMNS.find((entry) => entry.id === orderBy);
        return compareNumbers(a?.[column.key], b?.[column.key]);
    }
    return compareDates(a?.run_started_at, b?.run_started_at);
}

export default function SourceRunsTable({ runs, mediaId, sourceId }) {
    const intl = useIntl();
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState('started');
    const [order, setOrder] = useState('desc');
    const [page, setPage] = useState(1);

    useEffect(() => {
        setPage(1);
    }, [runs]);

    const handleSort = (columnId) => {
        const isAsc = orderBy === columnId && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(columnId);
        setPage(1);
    };

    const sortedRuns = useMemo(() => {
        return [...(runs ?? [])].sort((a, b) => {
            const result = compareRunRows(a, b, orderBy);
            if (result !== 0) {
                return applySortDirection(result, order);
            }
            return compareDates(b?.run_started_at, a?.run_started_at);
        });
    }, [runs, order, orderBy]);

    const pageCount = Math.max(1, Math.ceil(sortedRuns.length / PAGE_SIZE));

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const paginatedRuns = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return sortedRuns.slice(start, start + PAGE_SIZE);
    }, [sortedRuns, page]);

    const headerCellSx = {
        fontWeight: 600,
        color: 'text.secondary',
        borderColor: 'divider',
        bgcolor: 'background.paper',
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <TableContainer
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
            <Table
                stickyHeader
                size="small"
                aria-label={intl.formatMessage({ id: 'monitoring.source.title' })}
            >
                <TableHead>
                    <TableRow>
                        <SortableTableHeaderCell
                            columnId="started"
                            label={intl.formatMessage({ id: 'monitoring.source.column.started' })}
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={headerCellSx}
                        />
                        <SortableTableHeaderCell
                            columnId="finished"
                            label={intl.formatMessage({ id: 'monitoring.source.column.finished' })}
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={headerCellSx}
                        />
                        <SortableTableHeaderCell
                            columnId="status"
                            label={intl.formatMessage({ id: 'monitoring.source.column.status' })}
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={headerCellSx}
                        />
                        <SortableTableHeaderCell
                            columnId="ableToFetch"
                            label={intl.formatMessage({ id: 'monitoring.source.column.ableToFetch' })}
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={headerCellSx}
                        />
                        {NUMERIC_COLUMNS.map((column) => (
                            <SortableTableHeaderCell
                                key={column.id}
                                columnId={column.id}
                                label={intl.formatMessage({
                                    id: `monitoring.source.column.${column.id}`,
                                })}
                                orderBy={orderBy}
                                order={order}
                                onSort={handleSort}
                                align="right"
                                sx={headerCellSx}
                            />
                        ))}
                        <SortableTableHeaderCell
                            columnId="error"
                            label={intl.formatMessage({ id: 'monitoring.source.column.error' })}
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={headerCellSx}
                        />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedRuns.map((run) => (
                        <TableRow
                            key={run.id}
                            hover
                            onClick={() => {
                                if (mediaId && sourceId) {
                                    navigate(getRunDetailPath(mediaId, sourceId, run.id), {
                                        state: { run },
                                    });
                                }
                            }}
                            sx={{ cursor: mediaId && sourceId ? 'pointer' : 'default' }}
                        >
                            <TableCell sx={{ borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {formatDateTime(run.run_started_at, intl.locale)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {formatDateTime(run.run_finished_at, intl.locale)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ borderColor: 'divider' }}>
                                <StatusChip status={getRunDisplayStatus(run)} />
                            </TableCell>
                            <TableCell sx={{ borderColor: 'divider' }}>
                                <Typography variant="body2">
                                    {run.able_to_fetch_data === true
                                        ? intl.formatMessage({ id: 'monitoring.source.fetch.yes' })
                                        : run.able_to_fetch_data === false
                                          ? intl.formatMessage({ id: 'monitoring.source.fetch.no' })
                                          : '–'}
                                </Typography>
                            </TableCell>
                            {NUMERIC_COLUMNS.map((column) => (
                                <NumberCell
                                    key={column.id}
                                    value={run[column.key]}
                                    highlightColor={column.highlightColor}
                                />
                            ))}
                            <ErrorMessageCell value={run.error_summary} />
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            </TableContainer>
            {sortedRuns.length > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, flexShrink: 0 }}>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_event, value) => setPage(value)}
                        color="primary"
                        size="small"
                    />
                </Box>
            )}
        </Box>
    );
}
